/** @typedef {'development' | 'demo' | 'production' | 'local' | 'ci'} BuildEnvironment */

export const BUILD_ENV_LABELS = {
    development: 'Dev',
    demo: 'Demo',
    production: 'Production',
    local: 'Local',
    ci: 'CI',
};

/**
 * @param {string} iso
 * @returns {string}
 */
export function formatReleaseDate(iso) {
    if (!iso) {
        return '';
    }

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
        timeZoneName: 'short',
    }).format(date);
}

/**
 * @param {{
 *   environment?: string;
 *   version?: string;
 *   buildNumber?: string | number;
 *   buildTime?: string;
 * }} params
 * @returns {string}
 */
export function formatBuildFooter({
    environment = 'local',
    version = '0.0.0',
    buildNumber = 'local',
    buildTime = '',
} = {}) {
    const envLabel = BUILD_ENV_LABELS[environment] || environment;
    const versionLabel = `v${version}`;

    if (environment === 'local') {
        return `${versionLabel} · Local dev · Dev server`;
    }

    const buildLabel = `${envLabel} build ${buildNumber}`;
    const releaseLabel = formatReleaseDate(buildTime);

    if (releaseLabel) {
        return `${versionLabel} · ${buildLabel} · Released ${releaseLabel}`;
    }

    return `${versionLabel} · ${buildLabel}`;
}

/**
 * @param {Record<string, string | undefined>} [env]
 * @returns {{
 *   environment: string;
 *   environmentLabel: string;
 *   version: string;
 *   buildNumber: string;
 *   buildTime: string;
 *   footerText: string;
 *   titleText: string;
 * }}
 */
export function readBuildInfo(env = process.env) {
    const environment = env.VUE_APP_BUILD_ENV || 'local';
    const version = env.VUE_APP_APP_VERSION || '0.0.0';
    const buildNumber = String(env.VUE_APP_BUILD_NUMBER || 'local');
    const buildTime = env.VUE_APP_BUILD_TIME || '';

    const footerText = formatBuildFooter({
        environment,
        version,
        buildNumber,
        buildTime,
    });

    const titleText = buildTime
        ? `${footerText} (${buildTime})`
        : footerText;

    return {
        environment,
        environmentLabel: BUILD_ENV_LABELS[environment] || environment,
        version,
        buildNumber,
        buildTime,
        footerText,
        titleText,
    };
}
